import { useSearchParamsState } from 'hooks/useSearchParamState';
import { useStore } from 'hooks/useStore';
import {
  ConceptPayload,
  ConceptSuggestions,
  opportunityErrorDetailsInterface,
  Supplier,
  updateOpportunity,
} from 'Interfaces/BDPortalInterfaces';
import { cloneDeep, isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';
import {
  checkErrorForEditingOpportunitySave,
  createEmptyErrorDetailsObject,
  getErrorAccordionIndexesFromErrorDetails,
} from './utilities/save_validation';

const EditingOpportunityLogic = (
  opportunityIterationID: number,
  closeModal: (isNewIterationCreated?: boolean) => void,
  opportunityID: number
) => {
  const {
    UserStore: { userModulesRoles, userMetadata },
    BDPortalStore: {
      dropdownDetails,
      comments,
      getComments,
      saveErrorStatus,
      getConceptsSuggestions,
      getGFCNDCPacksize,
      iterationNdcPacksizeStrengths,
      isIterationNdcPacksizeStrengthsLoading,
      getConcepts,
      conceptSuggestions,
      concepts,
      currentConceptsIndex,
      setCurrentConceptsIndex,
      currentCommentsIndex,
      setCurrentCommentsIndex,
      saveComments,
      isCommentsDataLoading,
      saveConcept,
      isConceptSaving,
      board,
      isCommentSaving,
      isConceptsDataLoading,
      // editingOpportunity, setEditingOpportunity
    },
  } = useStore();
  const [editingOpportunity, setEditingOpportunity] = useState<updateOpportunity | null>(null);
  const [iterationType, setIterationType] = useState('');
  // since the props on the component are not separate in recursion so trying to solve it like this
  const [opportunityIterationIdState] = useState(opportunityIterationID);

  const [comment, setComment] = useState<string>('');
  const [initialOpportunity, setInitialOpportunity] = useState<updateOpportunity | null>(
    editingOpportunity ? JSON.parse(JSON.stringify(editingOpportunity)) : null
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [newPackageSize, setNewPackageSize] = useState<string | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [selectedConcepts, setSelectedConcepts] = useState<ConceptSuggestions[]>([]);
  const [isAddIterationModalVisibile, setIsAddIterationModalVisible] = useState(false);
  const [isNADWaiverModalOpen, setIsNADWaiverModelOpen] = useState(false);
  const [isSiteTransferWaiverModalOpen, setIsSiteTransferWaiverModelOpen] = useState(false);
  const [isOpportunityUpdating, setIsOpportunityUpdate] = useState(false);
  const [isSeeMoreIterationModalOpen, setIsSeeMoreIterationModalOpen] = useState<boolean>(false);
  const [moleculeName, setMoleculeName] = useState<string>('');
  const [isIterationOpen] = useSearchParamsState('isIterationOpen', '');
  const [oldIterationId, setOldIterationId] = useState<number | null>(null);
  const [isOldIterationModalVisible, setIsOldIterationModalVisibile] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [isIterationActive, setIsIterationActive] = useState(false);
  const [isOpportunityDetailsSaving, setIsOpportunityDetailsSaving] = useState(false);

  const [opportunityErrorDetails, setOpportunityErrorDetails] =
    useState<opportunityErrorDetailsInterface>(createEmptyErrorDetailsObject());
  const [openAccordionIndexes, setOpenAccordionIndexes] = useState<number[]>([]);
  const [accordionKeyIndex, setAccordionKeyIndex] = useState(1);
  const [
    editOpportunityConfirmationModalVisibility,
    setEditOpportunityConfirmationModalVisibility,
  ] = useState(false);
  const [isSaveTriggered, setIsSaveTriggered] = useState(false);
  const [tabKey, setTabKey] = useState<number>(1);
  const [charCount, setCharCount] = useState(0);
  const [isPortfolioOpportunity, setIsPortfolioOpportunity] = useState(false);

  // Regular expression to match base64 image
  const removeBase64Images = (value: string) => {
    // This regex will match base64 image data URLs like "data:image/png;base64,..."
    return value.replace(/data:image\/(png|jpg|jpeg|gif);base64,[^"]+/g, '');
  };

  // Handle changes in the editor
  const handleEditorChange = (value: string) => {
    const cleanText = removeBase64Images(value); // Remove base64 image datasetEditorText(value);
    if (cleanText === '<p><br></p>') {
      setCharCount(0);
    } else {
      setCharCount(cleanText.length); // Update character count, ignoring base64 data
    }
  };

  interface Option {
    label: string;
    value: string;
  }
  // A reference to store the latest AbortController instance
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchSuggestionsWithDelay = async (inputValue: string): Promise<Option[]> => {
    // Cancel any ongoing request if there is one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort the previous request
    }

    // Create a new AbortController instance for the new request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const response = await bdportalScreenServices.getSuppliersSuggestions(
            inputValue,
            controller.signal
          );

          const suggestions: Option[] = [];
          response.forEach(item => {
            if (
              item.SupplierName !== editingOpportunity?.PrimarySupplierID &&
              item.SupplierName !== editingOpportunity?.BackupSupplierID1 &&
              item.SupplierName !== editingOpportunity?.BackupSupplierID2
            ) {
              suggestions.push({
                label: item.SupplierName,
                value: item.SupplierName,
              });
            }
          });
          resolve(suggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          resolve([]); // Resolve with an empty array in case of an error
        }
      }, 500); // Delay of 500ms
    });
  };

  // Cleanup on component unmount (cancel any ongoing request)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSaveTask = async (task: updateOpportunity) => {
    setAccordionKeyIndex(accordionKeyIndex + 1);

    if (!editingOpportunity || !dropdownDetails) return;

    // error check
    const tempErrorDetails = checkErrorForEditingOpportunitySave(
      editingOpportunity,
      board === 'NC',
      dropdownDetails
    );
    setOpportunityErrorDetails(tempErrorDetails);
    const indexes = getErrorAccordionIndexesFromErrorDetails(tempErrorDetails);
    setOpenAccordionIndexes(indexes);

    // return if there is error in accordion values
    if (indexes.length > 0) {
      return;
    }

    // check if the opportunity estimated annual revenue, estimated annual gp are empty strings, update them with 0
    if (task.EstAnnualRevenue === '') {
      task.EstAnnualRevenue = '0';
    }
    if (task.EstGrossProfit === '') {
      task.EstGrossProfit = '0';
    }

    try {
      setIsOpportunityDetailsSaving(true);
      await bdportalScreenServices.updateBoard(task, iterationType);
      handleInitiateOpportunity();
      toast.success('Successfully saved Opportunity Details.');
      setIsSaveTriggered(true);
    } catch (error: unknown) {
      // Log error for debugging
      console.error('Failed to save the details:', error);
      toast.error(`Failed to save the details.`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      setIsOpportunityDetailsSaving(false);
      setTabKey(prev => prev + 1);
    }
  };

  const handleCheckIfSaveButtonIsDisabled = () => {
    if (
      !isIterationActive ||
      JSON.stringify(initialOpportunity) === JSON.stringify(editingOpportunity)
    ) {
      return true;
    }
    return false;
  };

  const handleInitiateOpportunity = () => {
    setInitialOpportunity(cloneDeep(editingOpportunity));
  };

  const handleOpenNADWaiverModal = () => {
    setIsNADWaiverModelOpen(true);
  };

  const handleCloseNADWaiverModal = () => {
    setIsNADWaiverModelOpen(false);
  };

  const handleOpenSiteTransferWaiverModal = () => {
    setIsSiteTransferWaiverModelOpen(true);
  };
  const handleCloseSiteTransferWaiverModal = () => {
    setIsSiteTransferWaiverModelOpen(false);
  };

  const handleOpenAddIterationModal = useCallback(() => {
    setIsAddIterationModalVisible(true);
  }, []);

  const handleCloseAddIterationModal = (newIterationCreated: boolean = false) => {
    setIsAddIterationModalVisible(false);
    if (newIterationCreated) {
      closeModal(true);
    }
  };

  const handleEditOpportunityModalClose = () => {
    if (handleCheckIfSaveButtonIsDisabled()) {
      closeModal(isSaveTriggered);
    }
    setEditOpportunityConfirmationModalVisibility(true);
  };

  const handleCancelForConfirmationModal = () => {
    setEditOpportunityConfirmationModalVisibility(false);
  };

  const handleConfirmForConfirmationModal = () => {
    setEditOpportunityConfirmationModalVisibility(false);
    closeModal(isSaveTriggered);
  };

  const handleCommentChange = (content: string) => {
    const normalizedContent = content.trim();
    setComment(normalizedContent === '<p><br></p>' ? '' : normalizedContent);
  };

  const handleSaveConcept = async (
    oppIID: number,
    strength: string,
    isActive: boolean,
    packageSize: string
  ) => {
    try {
      const payload: ConceptPayload[] = [
        {
          OpportunityIterationsID: oppIID,
          Strength: strength,
          IsActive: isActive,
          PackSize: packageSize,
        },
      ];

      const conceptStrength = concepts?.Concept_info.filter(
        item => item.Strength === strength
      )[0].PackSize.filter(item => item === packageSize);

      if (conceptStrength && conceptStrength.length === 0) {
        await saveConcept(payload);
        setEditIndex(null);
        setNewPackageSize('');
        await getConcepts(oppIID);
      } else {
        setNewPackageSize('');
        toast.warning('Package size already exists!!');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      toast.error(errorMessage, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    }
  };

  const isValidStatusMove = (currentStageID: number, newStageID: number) => {
    if (!userMetadata) return false;

    if (
      userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'developer' ||
      userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'management'
    ) {
      return true;
    }

    if (userMetadata?.roles.opptracker.length === 0) {
      return false;
    }

    if (
      userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'admin' ||
      userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'super'
    ) {
      return true;
    }

    if (currentStageID === newStageID) {
      return true;
    }

    if (currentStageID <= 3 && newStageID <= 3) {
      return true;
    }

    return false;
  };

  const createPayloads = () => {
    return selectedConcepts.map(concept => ({
      OpportunityIterationsID: editingOpportunity?.OpportunityIterationsID ?? 0,
      Strength: concept.Strength,
      PackSize: null,
      IsActive: true,
    }));
  };

  const filterExistingConcepts = () => {
    if (!concepts || !concepts.Concept_info) return conceptSuggestions; // Handle null cases

    const existingStrengths = new Set(concepts.Concept_info.map(concept => concept.Strength));
    const result = conceptSuggestions.filter(
      suggestion => !existingStrengths.has(suggestion.Strength)
    );
    return result.sort((a, b) => a.Strength.localeCompare(b.Strength));
  };

  const handleOldIterationClick = (iterationId: number) => {
    setOldIterationId(iterationId);
    setIsOldIterationModalVisibile(true);

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('opportunityIdParam', opportunityID.toString());
    newSearchParams.set('opportunityIterationIdParam', iterationId.toString());
    setSearchParams(newSearchParams);
  };

  const handleCloseOldIterationModal = () => {
    setOldIterationId(null);
    setIsOldIterationModalVisibile(false);

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('opportunityIdParam', opportunityID.toString());
    newSearchParams.set('opportunityIterationIdParam', opportunityIterationIdState.toString());
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    if (editingOpportunity) {
      if (editingOpportunity.IsActive) {
        setIsIterationActive(editingOpportunity.IsActive);
      } else {
        setIsIterationActive(false);
      }
    }
    if (isIterationOpen) {
      handleOpenAddIterationModal();
    }
  }, [editingOpportunity, isIterationOpen, handleOpenAddIterationModal]);

  const fetchOpportunityDetails = useCallback(async (signal: AbortSignal) => {
    if (opportunityIterationID && opportunityID) {
      try {
        const [, , , response] = await Promise.all([
          getComments(opportunityID, signal),
          getConcepts(opportunityIterationID, signal),
          getConceptsSuggestions(opportunityID, signal),
          bdportalScreenServices.getOpportunityByID(opportunityIterationID, signal),
          getGFCNDCPacksize(opportunityIterationID),
        ]);
        setIsPortfolioOpportunity(response.isPortfolioOpportunity);
        setIterationType(response.opportunity_type);
        const agency =
          response.opportunity_details.Agency &&
          JSON.parse(response.opportunity_details.Agency.replace(/'/g, '"'));
        setEditingOpportunity({
          ...response.opportunity_details,
          Agency: agency,
        });
        setInitialOpportunity({
          ...response.opportunity_details,
          Agency: agency,
        });
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.log(err);
        }
      }
    }
  }, [opportunityIterationID, opportunityID, getComments, getConcepts, getConceptsSuggestions, getGFCNDCPacksize]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchOpportunityDetails(signal);

    return () => {
      controller.abort();
    };
  }, [fetchOpportunityDetails]);

  useEffect(() => {
    if (concepts) setMoleculeName(concepts?.opportunityDetails.MoleculeAndForm || '');
  }, [concepts]);

  const checkSupplierOptionDisabled = (option: Supplier) => {
    const selected_options = [
      editingOpportunity?.PrimarySupplierID,
      editingOpportunity?.BackupSupplierID1,
      editingOpportunity?.BackupSupplierID2,
    ];
    if (selected_options.includes(option.SupplierName)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    setHasChanges(!isEqual(initialOpportunity, editingOpportunity));
  }, [editingOpportunity, initialOpportunity]);

  return {
    userModulesRoles,
    isIterationActive,
    editingOpportunity,
    opportunityIterationIdState,
    setEditingOpportunity,
    handleSaveTask,
    iterationType,
    isAddIterationModalVisibile,
    isNADWaiverModalOpen,
    isSiteTransferWaiverModalOpen,
    handleOpenNADWaiverModal,
    handleOpenSiteTransferWaiverModal,
    handleCloseNADWaiverModal,
    handleCloseSiteTransferWaiverModal,
    handleOpenAddIterationModal,
    handleCloseAddIterationModal,
    handleCheckIfSaveButtonIsDisabled,
    comment,
    setComment,
    setEditIndex,
    hasChanges,
    newPackageSize,
    editIndex,
    setNewPackageSize,
    setSelectedConcepts,
    selectedConcepts,
    isOpportunityUpdating,
    setIsOpportunityUpdate,
    moleculeName,
    setMoleculeName,
    isCommentsDataLoading,
    handleSaveConcept,
    isValidStatusMove,
    createPayloads,
    filterExistingConcepts,
    handleOldIterationClick,
    dropdownDetails,
    comments,
    saveErrorStatus,
    handleCommentChange,
    getConceptsSuggestions,
    getConcepts,
    conceptSuggestions,
    concepts,
    currentConceptsIndex,
    isOpportunityDetailsSaving,
    setCurrentConceptsIndex,
    currentCommentsIndex,
    setCurrentCommentsIndex,
    saveComments,
    saveConcept,
    isSeeMoreIterationModalOpen,
    setIsSeeMoreIterationModalOpen,
    oldIterationId,
    isOldIterationModalVisible,
    handleCloseOldIterationModal,
    checkSupplierOptionDisabled,
    isConceptSaving,
    opportunityErrorDetails,
    openAccordionIndexes,
    accordionKeyIndex,
    isCommentSaving,
    initialOpportunity,
    editOpportunityConfirmationModalVisibility,
    handleEditOpportunityModalClose,
    handleCancelForConfirmationModal,
    handleConfirmForConfirmationModal,
    fetchSuggestionsWithDelay,
    tabKey,
    isConceptsDataLoading,
    handleEditorChange,
    charCount,
    iterationNdcPacksizeStrengths,
    isIterationNdcPacksizeStrengthsLoading,
    isPortfolioOpportunity,
  };
};

export default EditingOpportunityLogic;
